<script setup lang="ts">
import { ref, computed } from 'vue';
import { useFontsStore } from '@/stores/fonts';
import { useModalStore } from '@/stores/modal';
import { useCalendarsStore } from '@/stores/calendars';
import type { ICalendar, INameValue, IFont } from '@/types';

import ModalConfirm from '@/components/ModalConfirm.vue';
import TextEdit from '@/components/TextEdit.vue';
import SelectEdit from '@/components/SelectEdit.vue';

const fontsStore = useFontsStore();
const modalStore = useModalStore();
const calendarsStore = useCalendarsStore();

fontsStore.loadFonts();
fontsStore.loadGoogleFonts();

const phrases = [
  '’Twas brillig, and the slithy toves  Did gyre and gimble in the wabe: All mimsy were the borogoves, And the mome raths outgrabe.',
  'The quick brown fox jumps over the lazy dog.',
  'Waltz, bad nymph, for quick jigs vex.',
  'Pack my box with five dozen liquor jugs.',
  'Five quacking zephyrs jolt my wax bed.',
  'The wizard quickly jinxed the gnomes before they vaporized.',
  'When zombies arrive, quickly fax judge Pat.',
  'Vintage images of the Missions Basilica San Diego de Alcala, San Luis Rey, and San Juan Capistrano, circa  1920-1930',
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 01234567890 $%&(),.',
];
const phrase = ref(phrases[7]);

const editFont = ref(0);
const fontSize = ref(16);
const searchText = ref('');
const calendarsUsingFont = ref([] as ICalendar[]);

const nameSortDirection = ref<'asc' | 'desc' | null>(null);
const usageSortDirection = ref<'asc' | 'desc' | null>(null);

function toggleNameSort() {
  nameSortDirection.value = nameSortDirection.value === 'asc' ? 'desc' : 'asc';
  usageSortDirection.value = null;
}

function toggleUsageSort() {
  usageSortDirection.value = usageSortDirection.value === 'asc' ? 'desc' : 'asc';
  nameSortDirection.value = null;
}

const sortedFonts = computed(() => {
  const fonts = fontsStore.fonts.filter((f) => f.name.toLowerCase().includes(searchText.value.toLowerCase()));

  if (nameSortDirection.value) {
    const direction = nameSortDirection.value === 'asc' ? 1 : -1;
    return [...fonts].sort((a, b) => direction * a.name.localeCompare(b.name));
  }

  if (usageSortDirection.value) {
    const direction = usageSortDirection.value === 'asc' ? 1 : -1;
    return [...fonts].sort((a, b) => direction * (a.usageCount - b.usageCount));
  }

  return fonts;
});

// <style>
//   @import url('https://fonts.googleapis.com/css2?family=Agbalumo&family=Cabin+Condensed&family=Cabin:ital,wght@1,400;1,500&display=swap');
// </style>

function insertGoogleFont(fontName: string) {
var newStyle = document.createElement('style');
      newStyle.appendChild(document.createTextNode("\
      @import url('https://fonts.googleapis.com/css2?family=" + fontName + "&display=swap');"));
      document.head.appendChild(newStyle);
}

function toggleEdit(fontId: number) {
  editFont.value = editFont.value === fontId ? 0 : fontId;
}

function truncate(text): any {
  if (text.length > 45) text = text.slice(0, 45) + '...';
  return text;
}

function textEditFontClose(content, id, property) {
  const font = fontsStore.fonts.find((f) => f.id == id);
  font[property] = content;
  fontsStore.saveFont(font);
  calendarsStore.refreshFonts();
}

function confirmDeleteFont(fontId: number) {
  modalStore.open('modalDeleteFont', { fontId: fontId });
}

function usageTooltip(font: IFont) {
  const maxRows = 12;
  const rows = font.usages
    .slice(0, maxRows)
    .map(
      (u) =>
        `<a href="/calendar/${u.calendarId}" target="_blank" class="usage-link">${u.calendarTitle}</a> &mdash; ${u.layoutItemName}`
    )
    .join('<br/>');
  const remaining = font.usages.length - maxRows;
  const moreText = remaining > 0 ? `<div class="mt-1 text-muted">&hellip; and ${remaining} more</div>` : '';
  return {
    content: `<div class="text-start usage-tooltip">${rows}${moreText}</div>`,
    html: true,
    triggers: ['click'],
    autoHide: true,
  };
}

function deleteFontClose(confirm: boolean) {
  if (confirm) {
    fontsStore.deleteFont(modalStore.data.fontId)
    .then((calendars) => {
      if(calendars){
        console.log(calendars[0])
        calendarsUsingFont.value = calendars;
        modalStore.open('modalDeleteFontError');
      } else {
        calendarsStore.refreshFonts();
      }
  });
  }
}

function fontInUseMessage(){
  if(calendarsUsingFont.value.length == 0){
    return "An unknown error occurred while deleting this font."
  }
  const limit = 5;
  let message = calendarsUsingFont.value
  .slice(0,limit)
  .reduce((a,c)=> a + '<br/>' + c.year + ' ' + c.title + ' <small>(' + c.identityName + ')</small>', '<strong>This font can\'t be deleted because it is in use by</strong>')
  if(calendarsUsingFont.value.length > limit){
    message += '<br/><strong>and ' + (calendarsUsingFont.value.length - limit) + ' more.</strong>'
  }
  return  message;
}

async function newFont() {
  fontsStore.newFont().then(function (fontId) {
    editFont.value = fontId;
    calendarsStore.refreshFonts();
  });
}

function googleFontSelected(fontFamily: string){
  const googleFont = fontsStore.googleFontsSelect.find((gFont)=> gFont.name == (fontFamily || 'Roboto'));
  return googleFont;
}

function googleFontVariantSelected(fontId: number){
  const font = fontsStore.fonts.find((f) => f.id == fontId);
  const googleFont = fontsStore.googleFontsSelect.find((gFont)=> gFont.name == (font.fontFamily || 'Roboto'));
  const variant = googleFont.options.find((f)=>f.name == font.fontWeight)
  return variant?.value || 0;
}

function selectGoogleFontClose(selectedOption, id) {
  const font = fontsStore.fonts.find((f) => f.id == id);
  const fontFamily = fontsStore.googleFontsSelect.find((gFont) => gFont.value == selectedOption.value)
  font.fontFamily = fontFamily.name;
  font.fontWeight = 'regular';
  fontsStore.saveFont(font);
  calendarsStore.refreshFonts();

  insertGoogleFont(fontFamily.name);
}

function selectGoogleFontVariantClose(selectedOption, id){
  const font = fontsStore.fonts.find((f) => f.id == id);
  const googleFont = fontsStore.googleFontsSelect.find((gFont)=> gFont.name == (font.fontFamily || 'Roboto'));
  const variant = googleFont.options.find((o)=>o.value == selectedOption.value);
  font.fontWeight = variant.name;
  console.log(variant)
  fontsStore.saveFont(font);
}
</script>

<template>
  <div class="container">
    <div class="row mt-3">
      <div class="col-12 col-4 d-flex align-items-center justify-content-between mb-3">
        <h3 class="mb-0">Fonts &amp; Styles</h3>
        <div class="ms-3" style="width: 400px;">
          <div class="d-flex justify-content-between">
            <small class="text-muted">{{ fontsStore.fonts.length }} / 200</small>
          </div>
          <div class="progress" style="height: 6px;" role="progressbar" :aria-valuenow="fontsStore.fonts.length" aria-valuemin="0" aria-valuemax="200">
            <div
              class="progress-bar"
              :class="fontsStore.fonts.length >= 200 ? 'bg-danger' : fontsStore.fonts.length >= 180 ? 'bg-warning' : 'bg-success'"
              :style="{ width: Math.min(100, (fontsStore.fonts.length / 200) * 100) + '%' }"
            ></div>
          </div>
        </div>
      </div>
      <div class="col-12 d-flex justify-content-end align-items-center">
        
        <button @click="newFont()" class="btn btn-outline-secondary btn-sm me-auto">NEW FONT</button>        
        <div class="btn-group btn-group-sm ms-3" role="group">
          <button @click="toggleNameSort()" type="button" class="btn" :class="nameSortDirection ? 'btn-secondary' : 'btn-outline-secondary'">
            NAME
            <i v-if="nameSortDirection === 'asc'" class="bi-sort-alpha-down"></i>
            <i v-else-if="nameSortDirection === 'desc'" class="bi-sort-alpha-up"></i>
            <i v-else class="bi-sort-alpha-down"></i>
          </button>
          <button @click="toggleUsageSort()" type="button" class="btn" :class="usageSortDirection ? 'btn-secondary' : 'btn-outline-secondary'">
            USAGES
            <i v-if="usageSortDirection === 'asc'" class="bi-sort-numeric-down"></i>
            <i v-else-if="usageSortDirection === 'desc'" class="bi-sort-numeric-up"></i>
            <i v-else class="bi-sort-numeric-down"></i>
          </button>
        </div>
        <strong class="ms-3">{{ fontSize }}pt</strong>
        <input style="width: 120px" v-model="fontSize" type="range" class="form-range ms-3" min="6" max="38" step="2" id="customRange3" />
        <select v-model="phrase" style="width: 260px" class="form-select form-select-sm ms-3">
          <option style="width: 200px" v-for="c in phrases" :value="c">{{ truncate(c) }}</option>
        </select>

        <div class="input-group input-group-sm ms-3" style="width:200px;">
          <span class="input-group-text" id="basic-addon1"><i class="bi-search"></i></span>
          <input type="text" v-model="searchText" class="form-control form-control-sm" placeholder="Font Name" aria-label="Search" aria-describedby="basic-addon1" />
          <button @click="searchText = ''" class="btn btn-secondary" id="basic-addon1"><i class="bi-x"></i></button>
        </div>
       
      </div>
    </div>
    <div class="row">
      <div class="col">
        <table class="table table-borderless">
          <thead>
            <tr>
              <th class="align-top" style="width: 20%"></th>
              <th class="align-top" style="width: 18%"></th>
              <th class="align-top" style="width: 18%"></th>
              <th class="align-top" style="width: 44%"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="font in sortedFonts" :key="font.id">
              <template v-if="editFont == font.id">
                <tr class="font-header-edit">
                  <td>
                    <label>NAME</label>
                    <TextEdit :text="font.name" :id="font.id.toString()" :property="'name'" @close="textEditFontClose"></TextEdit>
                  </td>
                  <td>
                    <label>GOOGLE FONT <a :href="'https://fonts.google.com/specimen/' + font.fontFamily.replace(' ','+')" target="_blank"><i class="bi bi-box-arrow-up-right"></i></a></label>
                    <SelectEdit :name-values="fontsStore.googleFontsSelect" :id="font.id.toString()" :selected-value="googleFontSelected(font.fontFamily).value" :always-editable="true" @close="selectGoogleFontClose"></SelectEdit>
                  </td>
                  <td>
                    <label>VARIANT</label>
                    <SelectEdit :name-values="googleFontSelected(font.fontFamily).options" :id="font.id.toString()" :selected-value="googleFontVariantSelected(font.id)" :always-editable="true" @close="selectGoogleFontVariantClose"></SelectEdit>
                 </td>
                  <td>
                    <label>OTHER STYLES</label>
                    <TextEdit :text="font.fontOther" :id="font.id.toString()" :property="'fontOther'" @close="textEditFontClose"></TextEdit>
                  </td>
                </tr>
                <tr class="font-sample-row" @click="toggleEdit(font.id)">
                  <td class="font-name-col selected">
                    <button v-if="font.usageCount > 0" type="button" @click.stop v-tooltip="usageTooltip(font)" class="usage-badge btn btn-sm">
                      {{ font.usageCount }} usage{{ font.usageCount === 1 ? '' : 's' }}
                    </button>
                    <button v-if="font.usageCount === 0" @click="confirmDeleteFont(font.id)" class="btn btn-sm btn-outline-secondary">
                      <i class="bi-trash"></i> REMOVE
                    </button>
                  </td>
                  <td colspan="3" class="font-sample-col selected">
                    <div :style="'font-size:' + fontSize + 'pt;font-family:' + font.fontFamily + ';font-weight:' + font.fontWeight + ';' + font.fontOther">{{ phrase }}</div>
                  </td>
                </tr>
              </template>
              <tr v-else class="font-header" @click="toggleEdit(font.id)">
                <td class="font-name-col">
                  <div class="font-name">{{ font.name }} {{ font.fontWeight ? '(' + font.fontWeight + ')' : '' }}</div>
                  <div class="font-name-meta">
                    <button v-if="font.usageCount > 0" type="button" @click.stop v-tooltip="usageTooltip(font)" class="usage-badge btn btn-sm">
                      {{ font.usageCount }} usage{{ font.usageCount === 1 ? '' : 's' }}
                    </button>
                  </div>
                </td>
                <td colspan="4" class="font-sample-col">
                  <div :style="'font-size:' + fontSize + 'pt;font-family:' + font.fontFamily + ';font-weight:' + font.fontWeight + ';' + font.fontOther">{{ phrase }}</div>
                </td>
              </tr>
              <tr class="spacer">
                <td colspan="100%"></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="column"></div>
    </div>
  </div>

  <ModalConfirm id="modalDeleteFont" @close="deleteFontClose" :message="`Are you sure you want to permenantly delete this font?`"></ModalConfirm>
  <ModalConfirm id="modalDeleteFontError" ref="modalDeleteFontError" :message="fontInUseMessage()" :title="'Error'" :ok-button="null" :close-button="{ label: 'Close', color: 'primary' }"></ModalConfirm>

</template>

<style lang="scss" scoped>
select,
option {
  width: 100px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.table {
  font-family: 'Cabin Condensed';

  td {
    cursor: pointer;
  }
}

tr.font-header {
  td {
    font-size: 1rem;
    background-color: #e9e9e9;
    vertical-align: middle;

    &.font-sample-col {
      background-color: #fff;
    }
  }
}

.font-name-col {
  white-space: nowrap;
  width: 1%;

  .font-name {
    font-weight: 600;
  }

  .font-name-meta {
    min-height: 1rem;
  }

  &.selected {
    background-color: #fff;
    vertical-align: top;
    padding-top: 0.5rem;
  }
}
tr.font-header-edit {
  td {
    height: 48px;
    padding-top: 0;
    font-size: 1rem;
    background-color: #e9e9e9;

    label {
      font-size: 0.7rem;
      vertical-align: bottom;
    }

    .font-name-meta {
      margin-top: 0.25rem;

      button {
        margin-top: 0;
      }
    }
  }
}
td.font-sample-col {
  background-color: #fff;

  div {
    padding: 0.25rem;
  }

  &.selected {
    padding: 0;
  }
}

tr.spacer td {
  padding: 0;
  height: 1px;
  background-color: #ddd;
}
</style>

<style lang="scss">
.usage-tooltip {
  .usage-link {
    color: inherit;
    text-decoration: underline;

    &:hover {
      color: inherit;
    }
  }
}


.usage-badge {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  padding: 0.1rem 0.4rem;
  line-height: 1.2;

  &:hover {
    color: #495057;
    background-color: #f1f1f1;
    border-color: #ddd;
  }
}

</style>
